import React, { useState } from "react";

interface ImageUploaderProps {
  onUpload: (url: string) => void;  
  onError?: (message: string) => void;  
  onUploadStart?: () => void;  
  label?: string; 
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUpload,
  onError,
  onUploadStart,
  label = "Upload Image"
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const imageServerUrl = process.env.REACT_APP_DUKE_IMAGE_SERVER_URL;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));

    // Set up form data for upload
    const formData = new FormData();
    formData.append("media", file);
    formData.append("folder", "tendo/product-images");
    formData.append("origin", "soko");

    // Validate server URL
    if (!imageServerUrl) {
      const errorMsg = "Image server URL is not defined.";
      console.error(errorMsg);
      onError?.(errorMsg);
      return;
    }

    try {
      // Notify parent component that upload is starting
      setLoading(true);
      onUploadStart?.();

      // Upload the image
      const response = await fetch(imageServerUrl, {
        method: "POST",
        body: formData,
      });

      // Handle response
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log("Upload response data:", responseData);

      // Extract URL from the correct response path
      let imageUrl = null;
      
      if (responseData.success && responseData.data && responseData.data.length > 0) {
        const dataItem = responseData.data[0];
        
        if (dataItem.imageLink) {
          imageUrl = dataItem.imageLink;
        } else {
          console.log("Data item structure:", dataItem);
          //fallback for url generation
          for (const key in dataItem) {
            if (typeof dataItem[key] === 'string' && 
               (dataItem[key].startsWith('http://') || dataItem[key].startsWith('https://'))) {
              imageUrl = dataItem[key];
              break;
            }
          }
        }
      }

      if (imageUrl) {
        onUpload(imageUrl); // Send URL to parent component
      } else {
        throw new Error(`Upload response did not contain a URL. Response: ${JSON.stringify(responseData)}`);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error during upload";
      console.error("Error uploading image:", errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Clean up preview URL when component unmounts
  React.useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  return (
    <div className="flex flex-col items-center gap-3 border p-4 rounded-lg shadow-md w-full max-w-xs">
      <label className={`cursor-pointer ${loading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'} text-white px-4 py-2 rounded-md transition w-full text-center`}>
        {loading ? "Uploading..." : label}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={loading}
        />
      </label>
      {preview && (
        <div className="mt-2 w-full">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-auto max-h-48 object-cover rounded-md border"
          />
        </div>
      )}
      {loading && (
        <div className="flex justify-center w-full mt-2">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;